// ============================================
// InventoryDriftPage
// Đối soát lệch tồn (sheet 2 — mục #27, #33)
// ============================================
//
// Nguồn: GET /inventory/drift/?days=N. Logic đối soát vốn đã chạy ở BE từ lâu
// nhưng kết quả CHỈ nằm trong log worker; endpoint này (03/09/2026) là lần đầu
// nó ra tới giao diện.
//
// Ý nghĩa dữ liệu: với mỗi cặp (sản phẩm, cửa hàng), so số bán ra ghi nhận từ
// đơn hàng với số thực sự bị trừ khỏi kho. Lệch dương = bán nhiều hơn trừ
// (kho đang báo thừa so với thực tế). Lệch âm = trừ nhiều hơn bán (thường là
// bút toán tay trùng, hoặc trừ hai lần).
//
// BẪY VẬN HÀNH quan trọng nhất của màn này: lịch chạy job nền nằm ở
// `manage.py setup_store_event_schedule`. Không chạy lệnh đó khi deploy thì
// endpoint trả rỗng — trông y hệt "kho khớp hoàn toàn". Vì vậy trạng thái rỗng
// ở đây KHÔNG nói "mọi thứ đều ổn" mà nói rõ cả hai khả năng.

import { useEffect, useMemo, useState } from 'react';
import type { InventoryDriftItem, InventoryDriftMeta } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import {
  PageContainer,
  PageHeader,
  SelectDropdown,
  DataTable,
  Pagination,
  type Column,
} from '../../components/shared';
import { useInventoryDrift } from '../../hooks';
import { useStoreSelectionStore } from '../../stores';
import { extractList } from '../../utils/apiList';

const PAGE_SIZE = 20;
const DAY_OPTIONS = [1, 7, 30];

/**
 * Tên field của bảng đối soát (bug #243).
 *
 * BE đính chính 18/09/2026 — dual-read của bản trước ĐANG TRƯỢT HẾT vì đoán
 * sai tên. Tên THẬT là `gap`, `sold`, `deducted`; các alias `drift_quantity`,
 * `sold_quantity`, `deducted_quantity` mới được thêm cùng đợt này.
 * `drift` — tên bản trước đọc đầu tiên — chưa bao giờ tồn tại, nên bốn cột số
 * trắng trơn là đúng.
 *
 * Cùng đợt BE thêm `product_name`, `product_sku`, `store_name`, và `detected_at`
 * (thời điểm chạy check). KHÔNG có `created_at` — đã bỏ nhánh đó.
 *
 * Giữ cả hai bộ tên cho tới khi BE lên QA: bản này còn ở nhánh local, QA hiện
 * tại vẫn chỉ có `gap`/`sold`/`deducted` và chưa có tên sản phẩm.
 */
type DriftRow = InventoryDriftItem & {
  /** Tên THẬT của chênh lệch (alias mới: `drift_quantity`). */
  gap?: number | null;
  drift_quantity?: number | null;
  /** Tên THẬT (alias mới: `sold_quantity`). */
  sold?: number | null;
  /** Tên THẬT (alias mới: `deducted_quantity`). */
  deducted?: number | null;
};

/**
 * Số hiển thị được, hoặc `null`.
 *
 * Nhận CẢ CHUỖI, không chỉ `number`. Bản trước kiểm `typeof c === 'number'` nên
 * mọi giá trị BE trả về dưới dạng chuỗi decimal của Django (`"5"`, `"5.000"`)
 * đều rơi về `null` và ba cột Sold / Deducted / Drift hiện `—` — trong khi dữ
 * liệu về đủ. Cùng một cái bẫy đã gây bug #242 ở màn thống kê hao hụt, chỉ khác
 * chỗ biểu hiện: ở kia in ra sai số, ở đây không in ra gì.
 *
 * `Number('')` ra `0` nên phải loại chuỗi rỗng TRƯỚC — nếu không ô trống sẽ
 * hiện thành số 0, tức là khẳng định "bán 0 cái" trong khi thật ra không biết.
 *
 * KHÔNG đổi `0` thành `null`: bán 0 và lệch 0 đều là kết quả hợp lệ.
 */
function readNumber(
  ...candidates: (number | string | null | undefined)[]
): number | null {
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === 'number') {
      if (Number.isFinite(c)) return c;
      continue;
    }
    const trimmed = String(c).trim();
    if (!trimmed) continue;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/** Số bán ra. Tên thật `sold` trước, alias `sold_quantity` sau. */
function readSold(row: DriftRow): number | null {
  return readNumber(row.sold, row.sold_quantity);
}

/** Số đã trừ kho. */
function readDeducted(row: DriftRow): number | null {
  return readNumber(row.deducted, row.deducted_quantity);
}

/**
 * Chênh lệch. `gap` là tên thật; `drift_quantity`/`drift` là alias.
 *
 * Không đọc được thì tự trừ từ hai số kia — chúng đủ để tính, không có lý do
 * gì để ô này trắng.
 */
function readGap(row: DriftRow): number | null {
  const direct = readNumber(row.gap, row.drift_quantity, row.drift);
  if (direct != null) return direct;
  const sold = readSold(row);
  const deducted = readDeducted(row);
  return sold != null && deducted != null ? sold - deducted : null;
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function InventoryDriftPage() {
  const { t } = useI18n();
  const selectedStoreId = useStoreSelectionStore((s) => s.selectedStoreId);

  const [days, setDays] = useState('1');
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useInventoryDrift({
    days: Number(days),
    store_id: selectedStoreId || undefined,
    page: currentPage,
    page_size: PAGE_SIZE,
  });

  const rows = useMemo(() => extractList<DriftRow>(data), [data]);

  /**
   * Chẩn đoán CHỈ Ở DEV: nếu có dòng mà cả ba cột số đều không đọc được thì in
   * ra key thật của payload.
   *
   * Endpoint này đã hai lần đổi tên field (`drift` → `gap`, rồi kiểu số →
   * chuỗi), và mỗi lần FE phải đoán lại mất một vòng trao đổi. Một dòng log
   * biến việc đó thành mở console xem tên field.
   */
  useEffect(() => {
    if (!import.meta.env.DEV || rows.length === 0) return;
    const broken = rows.find(
      (r) => readSold(r) == null && readDeducted(r) == null && readGap(r) == null
    );
    if (!broken) return;
    console.warn(
      '[StockDrift] Không đọc được cột số nào. Key thật trong payload:',
      Object.keys(broken),
      broken
    );
  }, [rows]);
  const totalItems =
    (data as { count?: number; meta?: { count?: number } } | undefined)?.count ??
    (data as { meta?: { count?: number } } | undefined)?.meta?.count ??
    rows.length;

  /**
   * Khung thời gian của lượt đối soát, lấy từ `meta` (BE 18/09/2026).
   *
   * Đáng hiện vì `days` người dùng chọn KHÔNG bằng khung thật: job chạy theo
   * lịch riêng, nên "1 ngày" có thể là cửa sổ kết thúc từ đêm qua. Không nói
   * ra thì bảng trống bị đọc thành "kho khớp", trong khi thật ra là "lần chạy
   * gần nhất chưa phủ tới hôm nay".
   */
  const window = (data as { meta?: InventoryDriftMeta } | undefined)?.meta;
  const totalPages = Math.max(Math.ceil(totalItems / PAGE_SIZE), 1);

  const columns: Column<DriftRow>[] = [
    {
      key: 'product',
      header: t('product'),
      render: (row) => (
        <div>
          {/* Không in UUID vào ô TÊN. Một chuỗi 36 ký tự ở chỗ đáng lẽ là tên
              sản phẩm chỉ khiến người dùng mang nó sang màn Products tra rồi
              không ra gì — đúng cái đã xảy ra ở bug #243. */}
          <p className="text-sm font-semibold text-slate-900">
            {row.product_name ?? (
              <span className="font-normal text-slate-400">{t('driftUnknownProduct')}</span>
            )}
          </p>
          <p className="text-xs text-slate-500">
            {row.product_sku ?? <span className="text-slate-400">ID: {row.product_id}</span>}
          </p>
        </div>
      ),
    },
    {
      key: 'store',
      header: t('storeWarehouseName'),
      width: '180px',
      render: (row) =>
        row.store_name ? (
          <p className="text-sm text-slate-700">{row.store_name}</p>
        ) : (
          <p className="text-sm text-slate-400">{t('driftUnknownStore')}</p>
        ),
    },
    {
      key: 'sold',
      header: t('driftSold'),
      width: '110px',
      render: (row) => (
        <p className="text-sm font-medium text-slate-700">{readSold(row) ?? '—'}</p>
      ),
    },
    {
      key: 'deducted',
      header: t('driftDeducted'),
      width: '120px',
      render: (row) => (
        <p className="text-sm font-medium text-slate-700">{readDeducted(row) ?? '—'}</p>
      ),
    },
    {
      key: 'drift',
      header: t('driftValue'),
      width: '110px',
      render: (row) => {
        const gap = readGap(row);
        if (gap == null) return <span className="text-sm text-slate-400">—</span>;
        return (
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${
              gap === 0
                ? 'bg-slate-100 text-slate-700'
                : gap > 0
                  ? 'bg-orange-50 text-orange-700'
                  : 'bg-red-50 text-red-700'
            }`}
          >
            {gap > 0 ? `+${gap}` : gap}
          </span>
        );
      },
    },
    {
      key: 'detected_at',
      header: t('driftDetectedAt'),
      width: '150px',
      // `detected_at` = thời điểm job chạy check. BE không trả `created_at`.
      render: (row) => (
        <p className="text-xs font-medium text-slate-600">{formatDateTime(row.detected_at)}</p>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader title={t('driftTitle')} subtitle={t('driftSubtitle')} />

      {(window?.window_start || window?.window_end) && (
        <p className="mt-2 text-xs text-slate-500">
          {t('driftWindow', {
            start: formatDateTime(window.window_start),
            end: formatDateTime(window.window_end),
          })}
        </p>
      )}

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {/* `ApiClientError` là object thường, không kế thừa `Error` — nên
              `instanceof Error` luôn sai và nuốt mất message thật của server.
              Cùng cái bẫy đã làm bug #244 mất công lần ra. */}
          {(error as { message?: string })?.message ||
            (error instanceof Error ? error.message : t('driftLoadError'))}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
          {t('driftDays')}
        </label>
        <SelectDropdown
          options={DAY_OPTIONS.map((d) => ({
            value: String(d),
            label: t('driftDaysOption').replace('{days}', String(d)),
          }))}
          value={days}
          onChange={(val) => {
            setDays(val);
            setCurrentPage(1);
          }}
          className="w-full sm:w-[200px]"
        />
      </div>

      {/* Trạng thái rỗng nói CẢ HAI khả năng — xem ghi chú đầu file. */}
      {!isLoading && !error && rows.length === 0 && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-sm font-semibold text-slate-700">{t('driftEmpty')}</p>
          <p className="mx-auto mt-2 max-w-xl text-xs text-slate-500">{t('driftEmptyHint')}</p>
        </div>
      )}

      {(isLoading || rows.length > 0) && (
        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(row: DriftRow) => `${row.product_id}-${row.store_id}`}
            isLoading={isLoading}
            showFilter={false}
            showHeaderMenu={false}
            emptyMessage={t('driftEmpty')}
            className="border-0 shadow-none"
          />
          {!isLoading && totalItems > 0 && (
            <div className="border-t border-slate-100">
              <Pagination
                variant="numbered"
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={PAGE_SIZE}
                onPageChange={setCurrentPage}
                itemLabel="products"
              />
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
}
